import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { Fragment } from 'preact';
import { useState, useEffect, useContext, useRef } from 'preact/hooks';
import { SDKContext } from '../context';
import { fireBeacon, applyCacheBuster, injectMacrosText, hasTextContent } from '../utils';
import { PLATFORM_MESSAGES } from '../constants';
import { useIsMobile } from '../utils/responsive';
import { useIsInView } from '../utils/useIsInView';
import { useCommercialAnimation, getCommercialStyles, } from '../utils/animations';
import { OfferModal } from './OfferModal';
import { OfferHeader } from './OfferHeader';
import { OfferImage } from './OfferImage';
import { OfferText } from './OfferText';
import { OfferCTAs } from './OfferCTAs';
import { ProgressBar } from './ProgressBar';
import { OfferwallCTA } from './OfferwallCTA';
import { OfferFooter } from './OfferFooter';
import { MiniText } from './MiniText';
import { TCButton } from './TCButton';
import { PerksWallet } from './PerksWallet';
import { getShowDelayMs } from '../utils/sdkRunTime';
import { openPublisherOfferwall } from '../utils/offerwall';
/**
 * A Preact component that displays a SOU (Single Offer Unit) modal ad.
 */
export function SingleOfferUnit() {
    const ctx = useContext(SDKContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [offers, setOffers] = useState([]);
    const [settings, setSettings] = useState(null);
    const [styles, setStyles] = useState(null);
    const [userAttributes, setUserAttributes] = useState(ctx?.sdk.userAttributes ?? {});
    const [pixelsFired, setPixelsFired] = useState(new Set());
    const isModalOpenRef = useRef(false);
    const showDelayTimerRef = useRef(null);
    const exitIntentListenerRef = useRef(null);
    const autoCloseTimerRef = useRef(null);
    isModalOpenRef.current = isModalOpen;
    const sendPlatformMessage = (eventName, eventData) => ctx?.sdk.sendMessage(eventName, eventData);
    useEffect(() => {
        if (!ctx)
            return;
        const { sdk, store } = ctx;
        const clearShowDelayTimer = () => {
            if (showDelayTimerRef.current != null) {
                clearTimeout(showDelayTimerRef.current);
                showDelayTimerRef.current = null;
            }
        };
        const clearExitIntentListener = () => {
            if (exitIntentListenerRef.current != null) {
                document.removeEventListener('mouseout', exitIntentListenerRef.current);
                exitIntentListenerRef.current = null;
            }
        };
        const openModal = () => {
            clearExitIntentListener();
            setIsModalOpen(true);
        };
        const isDesktopUA = () => !/Mobi|Tablet|iPad|iPhone|Android|Windows Phone/i.test(navigator.userAgent);
        const scheduleModal = (storeSettings) => {
            const useExitIntent = storeSettings.show_ads_on_exit &&
                !storeSettings.embedded?.enabled &&
                isDesktopUA();
            if (useExitIntent) {
                clearExitIntentListener();
                const listener = (e) => {
                    if (e.clientY < 0) {
                        openModal();
                    }
                };
                exitIntentListenerRef.current = listener;
                document.addEventListener('mouseout', listener);
                return;
            }
            openModal();
        };
        const handleStoreChange = () => {
            const storeOffers = store.getOffers();
            const storeSettings = store.getSettings();
            const storeStyles = store.getStyles();
            setOffers(storeOffers);
            setSettings(storeSettings);
            setStyles(storeStyles);
            const ready = storeOffers.length > 0 && storeSettings != null && storeStyles != null;
            if (!ready) {
                clearShowDelayTimer();
                clearExitIntentListener();
                setIsModalOpen(false);
                return;
            }
            if (isModalOpenRef.current) {
                clearShowDelayTimer();
                return;
            }
            if (!sdk.autoShow) {
                return;
            }
            const delayMs = getShowDelayMs(storeSettings);
            clearShowDelayTimer();
            clearExitIntentListener();
            if (delayMs === 0) {
                scheduleModal(storeSettings);
                return;
            }
            setIsModalOpen(false);
            showDelayTimerRef.current = setTimeout(() => {
                showDelayTimerRef.current = null;
                scheduleModal(storeSettings);
            }, delayMs);
        };
        const handleShow = () => {
            if (isModalOpenRef.current)
                return;
            const storeOffers = store.getOffers();
            const storeSettings = store.getSettings();
            const storeStyles = store.getStyles();
            const ready = storeOffers.length > 0 && storeSettings != null && storeStyles != null;
            if (ready) {
                scheduleModal(storeSettings);
            }
        };
        store.on('change', handleStoreChange);
        store.on('offers-updated', handleStoreChange);
        store.on('show', handleShow);
        handleStoreChange();
        setUserAttributes(sdk.userAttributes ?? {});
        return () => {
            clearShowDelayTimer();
            clearExitIntentListener();
            store.off('change', handleStoreChange);
            store.off('offers-updated', handleStoreChange);
            store.off('show', handleShow);
        };
    }, [ctx]);
    const isMobile = useIsMobile();
    // Commercial animation for professional offer transitions
    const animation = useCommercialAnimation(currentIndex);
    // Use displayIndex from animation state so content changes during fade-in, not immediately
    const currentOffer = offers[animation.displayIndex] || null;
    const [isInView, setViewRef] = useIsInView({
        isEmbedded: settings?.embedded?.enabled === true,
        resetKey: currentOffer?.id,
    });
    // Lead-in shows only on the pristine first view of offer 0. Keyed off the
    // animation's displayIndex (not currentIndex) so it stays mounted and fades
    // out together with the content, then unmounts exactly when the content swaps.
    // Driving it off currentIndex instead removed it ~150ms early — while offer 1
    // was still on screen — which read as the first offer jumping/reappearing.
    // The latch ref keeps it gone once content has moved past offer 0, so it never
    // returns when the user navigates back to the first offer. It's cleared when
    // the modal closes so the next open (a new session) starts pristine again -
    // the component instance itself persists across close/reopen, so without
    // this the latch would otherwise stick forever once tripped.
    const hasLeftFirstOfferRef = useRef(false);
    useEffect(() => {
        if (!isModalOpen) {
            hasLeftFirstOfferRef.current = false;
        }
    }, [isModalOpen]);
    if (animation.displayIndex > 0) {
        hasLeftFirstOfferRef.current = true;
    }
    const isFirstOfferFirstTime = !hasLeftFirstOfferRef.current && animation.displayIndex === 0;
    // Register handleClose callback with store
    useEffect(() => {
        if (!ctx)
            return;
        const { store } = ctx;
        const handleClose = () => {
            setIsModalOpen(false);
            if (currentOffer) {
                fireBeacon('close', currentOffer);
            }
            sendPlatformMessage(PLATFORM_MESSAGES.ADS_CLOSED.name, PLATFORM_MESSAGES.ADS_CLOSED);
            // Preact unmount is handled via setIsModalOpen(false); the store's
            // closeModal() also handles shadow-root-scoped DOM cleanup.
        };
        store.setModalCloseCallback(handleClose);
    }, [ctx, currentOffer]);
    useEffect(() => {
        const clearAutoCloseTimer = () => {
            if (autoCloseTimerRef.current != null) {
                clearTimeout(autoCloseTimerRef.current);
                autoCloseTimerRef.current = null;
            }
        };
        if (!isModalOpen) {
            clearAutoCloseTimer();
            return;
        }
        const duration = settings?.auto_close_offer_unit_duration;
        if (!duration || duration <= 0) {
            return;
        }
        autoCloseTimerRef.current = setTimeout(() => {
            autoCloseTimerRef.current = null;
            ctx?.store.closeModal();
        }, duration * 60 * 1000);
        return clearAutoCloseTimer;
    }, [isModalOpen, settings?.auto_close_offer_unit_duration, ctx]);
    const totalOffers = offers.length;
    const isLastOffer = currentIndex >= totalOffers - 1;
    const isFirstOffer = currentIndex === 0;
    if (!settings || !styles || !currentOffer) {
        return _jsx(Fragment, {});
    }
    const apiPayload = {
        ...userAttributes,
        sessionId: ctx?.store.getSessionId(),
        accountId: ctx?.sdk.accountId,
        awinAdvertiserId: ctx?.sdk.awinAdvertiserId,
        campaignId: ctx?.sdk.campaignId,
        pub_user_id: userAttributes.pub_user_id,
        theme_id: ctx?.sdk.theme_id,
        adpx_fp: ctx?.store.getFingerprint() ?? undefined,
    };
    const isUSPEnabled = settings.enable_user_selected_perks &&
        settings.enable_usp &&
        apiPayload?.pub_user_id;
    const shouldShowOfferwall = settings.enable_offerwall && settings.offerwall_show_cta;
    const shouldShowOfferwallInLeadIn = shouldShowOfferwall && !isUSPEnabled;
    const showImage = Boolean(currentOffer.image) && styles.offerText?.show_image !== false;
    const isStackedCTAs = styles.offerText?.cta_buttons_placement === 'stacked' && isMobile;
    // On the last offer, replace the negative CTA visually with the offerwall CTA.
    const lastOfferReplaceNeg = isLastOffer && shouldShowOfferwallInLeadIn;
    // When stacked (and not last offer replacing neg), offerwall CTA moves inline beside negative button.
    const offerwallInCTAs = isStackedCTAs && shouldShowOfferwallInLeadIn && !lastOfferReplaceNeg;
    const offerwallInProgressRow = shouldShowOfferwallInLeadIn && !offerwallInCTAs && !lastOfferReplaceNeg;
    const handleClose = () => {
        if (settings.embedded?.enabled)
            return;
        ctx?.store.closeModal();
    };
    const handleYesClick = () => {
        const offerData = {
            ...PLATFORM_MESSAGES.AD_TAKEN,
            ad_id: currentOffer.id,
            target_url: currentOffer.click_url,
            current: currentIndex + 1,
            total: totalOffers,
        };
        sendPlatformMessage(PLATFORM_MESSAGES.AD_TAKEN.name, offerData);
        if (ctx?.sdk.isWebView) {
            sendPlatformMessage(PLATFORM_MESSAGES.URL_CLICKED.name, {
                ...PLATFORM_MESSAGES.URL_CLICKED,
                target_url: currentOffer.click_url,
                external: settings.open_overlay_offerwall !== true,
            });
        }
        else {
            window.open(currentOffer.click_url, '_blank');
        }
        if (isLastOffer) {
            setTimeout(() => {
                handleClose();
            }, 1500);
        }
        else {
            handleNextOffer();
        }
    };
    const handleNoClick = () => {
        const offerData = {
            ...PLATFORM_MESSAGES.AD_NOT_TAKEN,
            ad_id: currentOffer.id,
            current: currentIndex + 1,
            total: totalOffers,
        };
        sendPlatformMessage(PLATFORM_MESSAGES.AD_NOT_TAKEN.name, offerData);
        fireBeacon('no_thanks_click', currentOffer);
        if (isLastOffer) {
            handleClose();
            return;
        }
        handleNextOffer();
    };
    const handleNextOffer = () => {
        if (currentIndex < totalOffers - 1) {
            setCurrentIndex(prev => prev + 1);
        }
    };
    const handlePreviousOffer = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };
    const handleOfferSelect = (index) => {
        if (index >= 0 && index < totalOffers) {
            setCurrentIndex(index);
        }
    };
    const handleLinkClick = (event) => {
        if (settings.embedded?.enabled) {
            event.preventDefault();
            event.stopPropagation();
            let targetUrl = '';
            if (event.target instanceof HTMLAnchorElement) {
                targetUrl = event.target.href;
            }
            sendPlatformMessage(PLATFORM_MESSAGES.URL_CLICKED.name, {
                ...PLATFORM_MESSAGES.URL_CLICKED,
                target_url: targetUrl,
                external: settings.open_overlay_offerwall !== true,
            });
            return false;
        }
        return true;
    };
    const handleHeaderClose = () => {
        if (settings.enable_offerwall && settings.open_offerwall && ctx) {
            const root = ctx.store.getShadowRoot() ?? document;
            openPublisherOfferwall({
                settings,
                styles,
                apiPayload,
                root,
                fromCloseButton: true,
                isWebView: ctx.sdk.isWebView,
                sendMessage: (n, d) => ctx.sdk.sendMessage(n, d),
                closeModal: () => {
                    void ctx.store.closeModal();
                },
            });
        }
        handleClose();
    };
    // Check if pixel has already been fired for this offer
    const hasPixelBeenFired = (offerId) => {
        return pixelsFired.has(offerId);
    };
    // Mark pixel as fired for this offer
    const markPixelAsFired = (offerId) => {
        setPixelsFired(prev => new Set(prev).add(offerId));
    };
    const showSouHeader = settings.embedded?.showHeader !== false;
    const showSouFooter = settings.embedded?.showFooter !== false;
    return (_jsxs(OfferModal, { isOpen: isModalOpen, onClose: handleClose, settings: settings, styles: styles, children: [currentOffer.pixel && isInView && !hasPixelBeenFired(currentOffer.id) && (_jsx("img", { style: { display: 'none' }, src: applyCacheBuster(currentOffer.pixel), alt: 'Pixel', onLoad: () => markPixelAsFired(currentOffer.id), onError: () => markPixelAsFired(currentOffer.id) })), currentOffer.adv_pixel_url &&
                isInView &&
                !hasPixelBeenFired(`${currentOffer.id}_adv`) && (_jsx("img", { style: { display: 'none' }, src: applyCacheBuster(currentOffer.adv_pixel_url), alt: 'Pixel', onLoad: () => markPixelAsFired(`${currentOffer.id}_adv`), onError: () => markPixelAsFired(`${currentOffer.id}_adv`) })), _jsxs("div", { ref: setViewRef, className: 'adpx_sou-grid', children: [showSouHeader && (_jsx(OfferHeader, { settings: settings, styles: styles, userAttributes: userAttributes, isFirstOffer: isFirstOffer, onClose: handleHeaderClose })), isFirstOfferFirstTime && styles.header?.lead_in_text && (_jsx("div", { id: 'adpx_lead-in', style: {
                            margin: '8px 0',
                            fontSize: `${styles.header?.headLineAndLeadInFontSize || 14}px`,
                            color: styles.header?.lead_in_text_color || '#000000',
                            fontFamily: styles.offerText?.font || 'Roboto, sans-serif',
                            padding: '0 24px',
                            textAlign: styles.header?.lead_in_alignment || 'start',
                            justifyContent: styles.header.lead_in_alignment,
                            ...getCommercialStyles(animation.contentOpacity),
                        }, children: injectMacrosText(styles.header.lead_in_text, userAttributes) })), _jsxs("div", { className: 'adpx_body-grid adpx_modal-content-container', "data-image-position": styles.popup?.image_position === 'left' ? 'left' : 'right', "data-has-image": showImage ? 'true' : 'false', children: [_jsx(OfferText, { offer: currentOffer, styles: styles, isMobile: isMobile, macroAttributes: userAttributes, isFirstOfferFirstTime: isFirstOfferFirstTime, contentStyle: getCommercialStyles(animation.contentOpacity) }), _jsx("div", { className: 'adpx_cta-area', style: getCommercialStyles(animation.ctaOpacity), children: _jsx(OfferCTAs, { offer: currentOffer, settings: settings, styles: styles, isLastOffer: isLastOffer, macroAttributes: userAttributes, onYesClick: handleYesClick, onNoClick: handleNoClick, offerwallNode: (offerwallInCTAs || lastOfferReplaceNeg) ? (_jsx(OfferwallCTA, { settings: settings, styles: styles, apiPayload: apiPayload })) : undefined, replaceNegWithOfferwall: !!lastOfferReplaceNeg }) }), showImage && (_jsx("div", { className: 'adpx_image-area', style: getCommercialStyles(animation.imageOpacity), children: _jsx(OfferImage, { offer: currentOffer, styles: styles, isMobile: isMobile, onYesClick: handleYesClick }) })), (settings.enable_pwaas_webhook || settings.enable_perkswallet) &&
                                currentOffer.campaign?.perkswallet_enabled &&
                                settings.perkswallet_show_cta && (_jsx("div", { className: 'adpx_perks-area', style: getCommercialStyles(animation.ctaOpacity), children: _jsx(PerksWallet, { offer: currentOffer, settings: settings, styles: styles, userAttributes: userAttributes }) }))] }), ((totalOffers > 1 &&
                        !(isMobile && settings.hide_progress_bar_mobile)) ||
                        offerwallInProgressRow) && (_jsxs("div", { id: 'adpx_sou-progress-offerwall-row', className: 'adpx_sou-progress-row', children: [totalOffers > 1 &&
                                !(isMobile && settings.hide_progress_bar_mobile) && (_jsx("div", { className: 'adpx_sou-progress-col', children: _jsx(ProgressBar, { settings: settings, styles: styles, currentIndex: currentIndex, totalOffers: totalOffers, hasOfferwallInProgressRow: Boolean(offerwallInProgressRow), offerwallAvailable: Boolean(shouldShowOfferwallInLeadIn), isMobileLayout: isMobile, onOfferSelect: handleOfferSelect, onPrevious: handlePreviousOffer, onNext: handleNextOffer }) })), offerwallInProgressRow && (_jsx("div", { id: 'adpx_sou-offerwall-cta-container', className: 'adpx_sou-offerwall-col', children: _jsx(OfferwallCTA, { settings: settings, styles: styles, apiPayload: apiPayload }) }))] })), (hasTextContent(currentOffer.mini_text_html) ||
                        hasTextContent(currentOffer.terms_and_conditions)) && (_jsxs("div", { id: 'adpx_sou-minitext-tnc-row', style: { display: 'flex', alignItems: 'center', justifyContent: !hasTextContent(currentOffer.mini_text_html) ? 'end' : '' }, children: [hasTextContent(currentOffer.mini_text_html) && (_jsx(MiniText, { offer: currentOffer, styles: styles })), hasTextContent(currentOffer.terms_and_conditions) && (_jsx("div", { id: 'adpx_tnc', style: { textAlign: 'right' }, children: _jsx(TCButton, { offer: currentOffer, styles: styles }) }))] })), showSouFooter && (_jsx(OfferFooter, { settings: settings, styles: styles, onLinkClick: handleLinkClick }))] })] }));
}
